import React from "react";
import styled, { css } from "styled-components";

export interface IconTabItem {
  key: string;
  label: string;
  icon: string;
  badge?: React.ReactNode;
}

interface IconTabListProps {
  items: IconTabItem[];
  activeKey?: string;
  onSelect: (key: string) => void;
  disabled?: boolean;
}

const IconTabList: React.FC<IconTabListProps> = ({ items, activeKey, onSelect, disabled }) => {
  return (
    <nav>
      {items.map((item) => {
        const active = item.key === activeKey;
        return (
          <TabButton
            key={item.key}
            type="button"
            disabled={disabled}
            $active={active}
            onClick={() => onSelect(item.key)}
          >
            <IconBadge $active={active}>
              <i className={item.icon} />
            </IconBadge>
            <span>{item.label}</span>
            {item.badge && <BadgeSlot>{item.badge}</BadgeSlot>}
          </TabButton>
        );
      })}
    </nav>
  );
};

const BadgeSlot = styled.span`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

export const IconBadge = styled.span<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 12px;
  font-size: 14px;
  transition: all 0.25s ease;
  color: ${(p) => (p.$active ? "#fff" : "rgba(130, 78, 235, 0.55)")};
  background: ${(p) => (p.$active ? "linear-gradient(135deg, #824eeb, #a97bf0)" : "#F5F2FE")};
`;

export const TabButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border: 1px solid transparent;
  border-radius: 14px;
  background: transparent;
  color: #6b6f7b;
  font-size: 14.5px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: all 0.25s ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &:not(:disabled):hover {
    background: rgba(130, 78, 235, 0.06);
    color: #1c1c30;
  }

  ${(p) =>
    p.$active &&
    css`
      background: rgba(130, 78, 235, 0.12);
      border-color: rgba(130, 78, 235, 0.35);
      color: #824eeb;
      box-shadow: 0 8px 18px -12px rgba(130, 78, 235, 0.5);
    `}
`;

export default IconTabList;
